class MobileMenuDrawer extends HTMLElement {
  connectedCallback() {
    /*
     * The drawer is authored inside the header section, whose wrapper is sticky and
     * whose <header> carries a backdrop-filter. Both of those change what a
     * `position: fixed` child measures itself against, and WebKit is stricter about it
     * than Chrome on desktop. Reparenting to <body> puts the panel and its backdrop in
     * the plainest possible ancestor chain, so their fixed positioning cannot be
     * captured by anything above them.
     *
     * Moving the element re-triggers this callback, hence the guard: without it every
     * listener below would be bound twice and the menu would open and close again on
     * one tap.
     */
    if (this.parentElement !== document.body) {
      // A re-render of the header section inserts a fresh drawer while the one moved
      // earlier is still on <body>. Drop the stale copy so the menu never doubles up
      // when the merchant edits the header in the theme editor.
      document.querySelectorAll('body > mobile-menu-drawer').forEach((old) => {
        if (old !== this) old.remove();
      });
      document.body.appendChild(this);
      return;
    }
    if (this.bound) return;
    this.bound = true;

    this.backdrop = this.querySelector('[data-mobile-menu-backdrop]');
    this.panel = this.querySelector('[data-mobile-menu-panel]');
    this.openTriggers = document.querySelectorAll('[data-mobile-menu-open]');
    this.closeTriggers = this.querySelectorAll('[data-mobile-menu-close]');

    this.openTriggers.forEach((trigger) => {
      trigger.addEventListener('click', () => this.open());
    });
    this.closeTriggers.forEach((trigger) => {
      trigger.addEventListener('click', () => this.close());
    });
    this.backdrop.addEventListener('click', () => this.close());

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.classList.contains('is-open')) this.close();
    });

    /*
     * iOS restores a page from the back/forward cache with the DOM exactly as it was
     * left, including the open class and the scroll lock. If the panel then fails to
     * paint, the page looks blank and cannot be scrolled — the state the shopper had to
     * reload out of. Closing on every pageshow means a restored page always starts from
     * a known state, and the scroll lock can never survive a navigation.
     */
    window.addEventListener('pageshow', () => this.close());
  }

  open() {
    this.classList.add('is-open');
    this.backdrop.dataset.open = 'true';
    document.documentElement.classList.add('scroll-locked');
    document.body.classList.add('scroll-locked');
    this.openTriggers.forEach((trigger) => trigger.setAttribute('aria-expanded', 'true'));
    const firstLink = this.panel.querySelector('a, button');
    if (firstLink) firstLink.focus({ preventScroll: true });
  }

  close() {
    this.classList.remove('is-open');
    this.backdrop.dataset.open = 'false';
    document.documentElement.classList.remove('scroll-locked');
    document.body.classList.remove('scroll-locked');
    this.openTriggers.forEach((trigger) => trigger.setAttribute('aria-expanded', 'false'));
  }
}

if (!customElements.get('mobile-menu-drawer')) {
  customElements.define('mobile-menu-drawer', MobileMenuDrawer);
}
